<template>
  <div>
    <Head :title="title" />
    <div class="mb-8 flex justify-start max-w-full flex-wrap">
      <h1 class="font-bold text-3xl">
        <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('admin')">
          Admin
        </inertia-link>
        <span class="text-indigo-400 font-medium">/</span>
        {{ title }}
      </h1>
    </div>

    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md">
        <label class="block text-gray-700">Status:</label>
        <select v-model="form.status" class="form-select">
          <option :value="null" />
          <option value="active">
            Aktif
          </option>
          <option value="inactive">
            Tidak Aktif
          </option>
          <option value="open">
            Open
          </option>
          <option value="closed">
            Closed
          </option>
        </select>
      </search-filter>

      <inertia-link
        :href="route('admin.portal-announcements.create')"
        class="btn-indigo">
        <span>Buat Pengumuman</span>
      </inertia-link>
    </div>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6">
              Judul
            </th>
            <th class="pb-4 pt-6 px-6">
              Status Portal
            </th>
            <th class="pb-4 pt-6 px-6">
              Aktif
            </th>
            <th class="pb-4 pt-6 px-6">
              Jadwal
            </th>
            <th class="pb-4 pt-6 px-6">
              Aksi
            </th>
            <th class="pb-4 pt-6 px-6">
              Dibuat Oleh
            </th>
            <th class="pb-4 pt-6 px-6">
              Diupdate pada
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="announcement in announcements.data"
            :key="announcement.id"
            class="hover:bg-gray-50 focus-within:bg-gray-50">
            <td class="border-t">
              <div class="px-6 py-4">
                {{ announcement.title }}
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-semibold rounded-full"
                  :class="{
                    'bg-green-100 text-green-800':
                      announcement.portal_status === 'open',
                    'bg-red-100 text-red-800':
                      announcement.portal_status === 'closed',
                  }">
                  {{
                    announcement.portal_status === "open" ? "Open" : "Closed"
                  }}
                </span>
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-semibold rounded-full"
                  :class="{
                    'bg-green-100 text-green-800': announcement.is_active,
                    'bg-red-100 text-red-800': !announcement.is_active,
                  }">
                  {{ announcement.is_active ? "Aktif" : "Tidak Aktif" }}
                </span>
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                <div
                  v-if="
                    announcement.scheduled_open_at ||
                      announcement.scheduled_close_at
                  ">
                  <div
                    v-if="announcement.scheduled_open_at"
                    class="text-gray-600">
                    <span class="font-semibold">Buka:</span>
                    {{ formatDate(announcement.scheduled_open_at) }}
                  </div>
                  <div
                    v-if="announcement.scheduled_close_at"
                    class="text-gray-600">
                    <span class="font-semibold">Tutup:</span>
                    {{ formatDate(announcement.scheduled_close_at) }}
                  </div>
                </div>
                <div v-else class="text-gray-400">
                  -
                </div>
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4 flex items-center">
                <button
                  v-if="!announcement.is_active"
                  @click="activate(announcement.id)"
                  class="mr-3 text-green-500 hover:text-green-700"
                  title="Aktifkan">
                  <icon name="check-circle" class="w-5 h-5" />
                </button>
                <button
                  v-if="announcement.is_active"
                  @click="deactivate(announcement.id)"
                  class="mr-3 text-red-500 hover:text-red-700"
                  title="Nonaktifkan">
                  <icon name="x-circle" class="w-5 h-5" />
                </button>
                <button
                  @click="preview(announcement.id)"
                  class="mr-3 text-gray-400 hover:text-gray-600"
                  title="Preview">
                  <icon name="eye" class="w-5 h-5" />
                </button>
                <inertia-link
                  :href="route('admin.portal-announcements.edit', announcement.id)"
                  class="mr-3 text-gray-400 hover:text-gray-600"
                  title="Edit">
                  <icon name="pencil" class="w-5 h-5" />
                </inertia-link>
                <button
                  @click="destroy(announcement.id)"
                  class="mr-3 text-gray-400 hover:text-gray-600"
                  title="Hapus">
                  <icon name="trash" class="w-5 h-5" />
                </button>
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                {{
                  announcement.creator
                    ? `${announcement.creator.first_name} ${announcement.creator.last_name}`
                    : "-"
                }}
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                {{ formatDate(announcement.updated_at) }}
              </div>
            </td>
          </tr>
          <tr v-if="announcements.data.length === 0">
            <td class="border-t px-6 py-4" colspan="7">
              Tidak ada pengumuman.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination :links="announcements.links" />
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import { format, parseISO } from 'date-fns'
import { useForm } from '@inertiajs/vue2'
import { Head, Link } from '@inertiajs/vue2'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Icon,
    'inertia-link': Link,
    SearchFilter,
    Pagination,
  },

  props: {
    announcements: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },

  layout: Layout,

  data () {
    return {
      title: 'Portal Pengumuman',
      form: useForm({
        search: this.filters.search,
        status: this.filters.status,
      }),
    }
  },

  watch: {
    form: {
      handler () {
        this.form.get(this.route('admin.portal-announcements.index'), {
          preserveState: true,
          preserveScroll: true,
        })
      },
      deep: true,
    },
  },

  methods: {
    activate (id) {
      Swal.fire({
        title: 'Aktifkan Pengumuman?',
        text: 'Pengumuman aktif lainnya akan dinonaktifkan.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya, aktifkan!',
        cancelButtonText: 'Batal',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.post(
            this.route('admin.portal-announcements.activate', id),
            {},
            {
              preserveScroll: true,
            },
          )
        }
      })
    },

    deactivate (id) {
      Swal.fire({
        title: 'Nonaktifkan Pengumuman?',
        text: 'Apakah Anda yakin ingin menonaktifkan pengumuman ini?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya, nonaktifkan!',
        cancelButtonText: 'Batal',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.post(
            this.route('admin.portal-announcements.deactivate', id),
            {},
            {
              preserveScroll: true,
            },
          )
        }
      })
    },

    async preview (id) {
      try {
        const response = await axios.get(
          `/admin/portal-announcements/${id}/preview-token`,
        )
        const token = response.data.token
        const url = this.route('admin.portal-announcements.preview', {
          id,
          token,
        })
        window.open(url, '_blank')
      } catch (error) {
        console.error('Failed to generate preview token:', error)
        Swal.fire(
          'Gagal',
          'Gagal membuat preview token. Silakan coba lagi.',
          'error',
        )
      }
    },

    destroy (id) {
      Swal.fire({
        title: 'Hapus Pengumuman?',
        text: 'Apakah Anda yakin ingin menghapus pengumuman ini?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ya, hapus!',
        cancelButtonText: 'Batal',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.delete(
            this.route('admin.portal-announcements.destroy', id),
            {
              preserveScroll: true,
            },
          )
        }
      })
    },

    formatDate (date) {
      if (!date) return '-'
      return format(parseISO(date), 'dd/MM/yyyy HH:mm:ss')
    },
  },
}
</script>
